import type { HandView, LegalActions } from "@numbet/engine";
import type { ClientMessage } from "@numbet/protocol";
import { useState } from "react";
import { formatChips } from "../format.ts";
import { t } from "../i18n.ts";

interface Props {
  legal: LegalActions;
  hand: HandView;
  potTotal: number;
  myRoundBet: number;
  send: (message: ClientMessage) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Gesperrte Knöpfe, solange ein anderer am Zug ist (Entwurf 7a). */
export function IdleActions() {
  return (
    <div className="actions" aria-hidden="true">
      <button type="button" className="btn" disabled>
        {t.fold}
      </button>
      <button type="button" className="btn" disabled>
        {t.call("").trim()}
      </button>
      <button type="button" className="btn" disabled>
        {t.raise}
      </button>
    </div>
  );
}

/** Aktionen am Zug (Entwurf 1b): Aussteigen · Mitgehen/Schieben · Erhöhen auf … mit Schnellwahl. */
export function ActionBar({ legal, hand, potTotal, myRoundBet, send: sendNow }: Props) {
  const min = legal.minRaiseTo;
  const max = legal.maxRaiseTo;
  const [target, setTarget] = useState(min ?? 0);
  const [adjust, setAdjust] = useState(false);
  // Nach einem Klick sperren, bis die Antwort des Servers da ist (verhindert doppelte Aktionen)
  const [pending, setPending] = useState(false);
  const send = (message: ClientMessage) => {
    if (pending) return;
    setPending(true);
    sendNow(message);
    setTimeout(() => setPending(false), 3000);
  };

  const isBet = hand.currentBet === 0;
  const canRaise = min !== null && max !== null;
  const value = canRaise ? clamp(target, min, max) : 0;
  const allIn = canRaise && value === max;
  // Pot-Größen: Erhöhung um den Pot nach dem eigenen Mitgehen
  const potAfterCall = potTotal + legal.callAmount;
  const preset = (fraction: number) => (canRaise ? clamp(hand.currentBet + potAfterCall * fraction, min, max) : 0);
  const presets: [string, number][] = canRaise
    ? [
        [t.min, min],
        [t.halfPot, preset(0.5)],
        [t.fullPot, preset(1)],
        [t.allIn, max],
      ]
    : [];
  const shortCall = legal.callAmount > 0 && legal.callAmount + myRoundBet < hand.currentBet;

  return (
    <div className="stack" style={{ gap: 10 }} aria-busy={pending}>
      <div className="actions">
        <button type="button" className="btn btn-dark" disabled={pending} onClick={() => send({ type: "fold" })}>
          {t.fold}
        </button>
        {legal.canCheck ? (
          <button type="button" className="btn btn-cyan" disabled={pending} onClick={() => send({ type: "check" })}>
            {t.check}
          </button>
        ) : (
          <button type="button" className="btn btn-cyan" disabled={pending} onClick={() => send({ type: "call" })}>
            {shortCall ? `${t.allIn} ${formatChips(legal.callAmount)}` : t.call(formatChips(legal.callAmount))}
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canRaise || pending}
          onClick={() => send(allIn ? { type: "allIn" } : { type: "bet", to: value })}
        >
          <span>
            {allIn ? t.allIn : isBet ? t.bet : t.raise}
            <span className="sub">{canRaise ? `${isBet && !allIn ? "" : "auf "}${formatChips(value)}` : ""}</span>
          </span>
        </button>
      </div>
      {canRaise && (
        <div className="bet-presets">
          {presets.map(([label, amount]) => (
            <button key={label} type="button" className="btn btn-outline" aria-pressed={amount === value} onClick={() => setTarget(amount)}>
              {label}
            </button>
          ))}
          {min !== max && (
            <button type="button" className="btn btn-outline" aria-pressed={adjust} aria-label={t.adjust} title={t.adjust} onClick={() => setAdjust((v) => !v)}>
              ±
            </button>
          )}
        </div>
      )}
      {canRaise && adjust && min !== max && (
        <input
          className="range"
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          aria-label={t.amount}
          onChange={(e) => setTarget(Number(e.target.value))}
        />
      )}
    </div>
  );
}
